import React from "react"

import {
	Box,
	ToggleButtonGroup
} from "@mui/material"

import {
	ErrorResponse,
	Loading,
	RestResponse,
} from "@sinossi/mates-react-library"

import { Trans } from "@lingui/macro"
import { BrandForm } from "components/brand"
import CompatibleTable from "components/brand/compatible-table/compatibleTable"
import {
	PageHeader,
	RoutesData,
	SbToggleButton,
} from "components/commons"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import {
	AssetMatchBrand,
	Brand,
	BrandProfileUpdateRequest,
	Company,
	ContractResponse,
	ContractsBrandRequest,
	CreateRepresentativeRequest,
	Document,
	OrderType,
	PaginatedResponse,
	Product,
	RequisiteRequest,
	SearchFilter,
	SessionResponse
} from "models"
import { BrandManagementServices } from "services/brand-management.services"
import { BrandProfileServices } from "services/brand-profile.services"
import withUseRouter, { UseRouterParams } from "services/router"
import Representatives from "../../components/brand/representatives/representatives"

interface BrandProfileProps extends UseRouterParams {
	sessionData: SessionResponse
}
interface BrandProfileState extends MatesComponentState {
	loading: boolean

	brandData: Brand | null

	documentsFilter: SearchFilter
	documentsData: PaginatedResponse<Document> | null
	contractsData: PaginatedResponse<ContractResponse> | null

	companyList: PaginatedResponse<Company> | null
	productList: PaginatedResponse<Product> | null
	logo: FormData | null
	compatibleData: any | null
	currentTab: string
}
class BrandProfile extends React.Component<BrandProfileProps, BrandProfileState> {
	private readonly matesComponent: MatesComponent<BrandProfileProps, BrandProfileState> = new MatesComponent(this)
	private readonly brandId: string

	constructor(props: BrandProfileProps) {
		super(props)

		this.brandId = this.props.params.brandId

		this.state = {
			...this.matesComponent.setupState(),
			loading: true,
			brandData: null,
			documentsFilter: new SearchFilter(),
			contractsData: null,
			documentsData: null,
			companyList: null,
			productList: null,
			logo: null,
			compatibleData: null,
			currentTab: "1"
		}
	}

	componentDidMount() {
		this.getCompany()
	}

	private getCompany = (): void => {
		BrandManagementServices.getCompany(this.getCompanyCallback, this.getCompanyCallbackError)
	}

	private getCompanyCallback = (response: RestResponse<PaginatedResponse<Company>, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(() => ({ companyList: response.data, }), this.getProduct)
	}

	private getCompanyCallbackError = (response: RestResponse<PaginatedResponse<Company>, ErrorResponse>): void => {
		this.setState(() => ({ companyList: null, }))
		this.matesComponent.onException(response)
	}

	private getProduct = (): void => {
		BrandManagementServices.getProducts(this.getProductsCallback, this.getProductCallbackError)
	}

	private getProductsCallback = (response: RestResponse<PaginatedResponse<Product>, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(() => ({ productList: response.data, }), this.getBrand)
	}

	private getProductCallbackError = (response: RestResponse<PaginatedResponse<Product>, ErrorResponse>): void => {
		this.setState(() => ({ productList: null, }))
		this.matesComponent.onException(response)
	}

	private getBrand = () => {
		BrandManagementServices.getBrand(this.brandId, this.getBrandCallback, this.getBrandCallbackError)
	}

	private getBrandCallback = (response: RestResponse<Brand, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(() => ({ brandData: response.data }), this.getDocuments)
	}

	private getBrandCallbackError = (response: RestResponse<Brand, ErrorResponse>): void => {
		this.setState(() => ({ brandData: null, }))
		this.matesComponent.onException(response)
	}

	private getDocuments = () => {
		BrandProfileServices.getDocuments(
			this.brandId,
			this.state.documentsFilter,
			this.getDocumentsCallback,
			this.getDocumentsCallbackError
		)
	}

	private getDocumentsCallback = (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(() => ({
			documentsData: response.data
		}), () => this.getMatch(new SearchFilter()))
	}

	private getDocumentsCallbackError = (response: RestResponse<PaginatedResponse<Document>, ErrorResponse>) => {
		this.setState(() => ({ documentsData: null, }))
		this.matesComponent.onException(response)
	}

	private getMatch = (request: SearchFilter) => {
		BrandProfileServices.getMatch(this.brandId, request, this.getMatchCallback, this.matesComponent.onException)
	}

	private getMatchCallback = (response: RestResponse<PaginatedResponse<AssetMatchBrand>, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		const contractsRequest = {
			pageSize: 10,
			pageId: 0,
			orderType: OrderType.ASCENDING,
			orderBy: "id",
			brandId: this.brandId
		}

		this.setState(() => ({ compatibleData: response.data }), () => this.getContracts(contractsRequest))
	}

	private submitForm = (request: BrandProfileUpdateRequest, logo: FormData | null) => {
		this.setState(() => ({
			loading: true,
			logo: logo,
		}),
			() => BrandProfileServices.updateBrand(
				this.brandId,
				request,
				this.updateBrandCallback,
				this.matesComponent.onException,
			),
		)
	}

	private updateBrandCallback = (response: RestResponse<any, ErrorResponse>): void => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.updateLogo()
	}

	private updateLogo = () => {
		if (!this.state.logo) {
			this.getBrand()
			return
		}

		BrandManagementServices.updateLogo(
			this.brandId,
			this.state.logo,
			this.updateLogoCallback,
			this.matesComponent.onException,
		)
	}

	private updateLogoCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.getBrand()
	}

	private callbackDelete = (documentId: string) => {
		if (this.state.brandData) {
			BrandProfileServices.deleteDocument(
				this.state.brandData.id,
				documentId,
				this.onDeleteCompleted,
				this.matesComponent.onException
			)
		}
	}

	private onDeleteCompleted = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(
			() => ({
				loading: true,
				documentsFilter: {
					...this.state.documentsFilter,
					pageId: 0,
				},
			}),
			this.getDocuments
		)
	}

	private callbackUpload = (data: FormData) => {
		if (this.state.brandData) {
			BrandProfileServices.uploadDocument(this.state.brandData.id, data, this.uploadCallback, this.matesComponent.onError)
		}
	}

	private uploadCallback = (response: any) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(
			() => ({
				loading: true,
				documentsFilter: {
					...this.state.documentsFilter,
					pageId: 0,
				},
			}),
			this.getDocuments
		)
	}

	private callbackMatch = (request: SearchFilter) => {
		this.getMatch(request)
	}

	private onPaginationChanged = (pageId: number, pageSize: number): void => {
		this.setState(
			() => ({
				documentsFilter: {
					...this.state.documentsFilter,
					pageId: pageId,
					pageSize: pageSize,
				}
			}),
			this.getDocuments
		)
	}
	private onSortChanged = (orderBy: string | null, orderType: OrderType | null): void => {
		this.setState(
			() => ({
				documentsFilter: {
					...this.state.documentsFilter,
					orderBy: orderBy,
					orderType: orderType,
				}
			}),
			this.getDocuments
		)
	}

	private callbackContracts = (value: SearchFilter) => {
		let request: ContractsBrandRequest = {
			...value,
			brandId: this.brandId
		}

		this.getContracts(request)
	}

	private getContracts = (request: ContractsBrandRequest) => {
		BrandProfileServices.getContracts(this.brandId, request, this.getContractsCallback, this.matesComponent.onError)
	}

	private getContractsCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onException(response)
			return
		}

		this.setState(() => ({
			contractsData: {
				items: response.data.items,
				pagination: response.data.pagination,
			},
			loading: false
		}))
	}

	private updateRepresentative = (rep: CreateRepresentativeRequest) => {

		let products: string[] = []
		let requisites: RequisiteRequest[] = []

		this.state.brandData?.products.map((product) => products.push(product.id))
		this.state.brandData?.requisites.requisites.map((requisite) => requisites.push({
			preferredContractTypes: requisite.preferredContractTypes,
			preferredAssetType: requisite.preferredAssetType,
			minGla: requisite.minGla,
			maxGla: requisite.maxGla,
			minLandArea: requisite.minLandArea,
			maxLandArea: requisite.maxLandArea,
			positionType: requisite.positionType,
			shopWindows: requisite.shopWindows,
			singleLevel: requisite.singleLevel
		}))

		const request: BrandProfileUpdateRequest = {
			name: this.state.brandData?.name,
			company: this.state.brandData?.company.id,
			representative: rep,
			products: products,
			requisites: requisites
		}

		this.submitForm(request, null)
	}

	private onTabChange = (_: any, newValue: string) => {
		if (newValue) {
			this.setState(() => ({ currentTab: newValue }))
		}
	}

	render() {
		return (
			<Box sx={{ pt: 2 }}>
				{this.matesComponent.render()}

				<Loading show={this.state.loading} />
				<PageHeader
					title={RoutesData.BRAND_DETAILS.title}
					paths={[RoutesData.DASHBOARD, RoutesData.BRAND_LIST, RoutesData.BRAND_DETAILS,]}
					params={{ "BRAND_DETAILS": { "id": this.brandId, } }}
				/>
				<Box textAlign={"right"} sx={{ mb: 2 }}>
					<ToggleButtonGroup
						value={this.state.currentTab}
						exclusive
						onChange={this.onTabChange}
						aria-label="text alignment"
					>
						<SbToggleButton value="1">
							<Trans id="brand-profile.details">
								Dettagli
							</Trans>
						</SbToggleButton>
						<SbToggleButton value="2">
							<Trans id="brand-profile.representative">
								Referenti
							</Trans>
						</SbToggleButton>
					</ToggleButtonGroup>
				</Box>

				{
					this.state.currentTab === "1" && !this.state.loading &&
					<Box>
						<BrandForm
							brand={this.state.brandData}
							companiesList={this.state.companyList?.items ?? []}
							productsList={this.state.productList?.items ?? []}
							submitForm={this.submitForm}
						/>
						{/* <Box sx={{ mt: 5 }}>
							<BrandDocumentsTable
								data={this.state.documentsData}
								brandId={this.brandId}
								pageSize={this.state.documentsFilter.pageSize}
								onPaginationChanged={this.onPaginationChanged}
								onSortChanged={this.onSortChanged}
								callbackUpload={this.callbackUpload}
								callbackDelete={this.callbackDelete}
								sessionData={this.props.sessionData}
							/>
						</Box> */}
						{/* {
							this.state.contractsData &&
							<Box sx={{ mt: 5 }}>
								<ContractsTable contracts={this.state.contractsData} handleCallback={this.callbackContracts} />
							</Box>
						} */}
						{
							this.state.compatibleData &&
							<Box sx={{ mt: 5 }}>
								<CompatibleTable data={this.state.compatibleData}
									handleCallback={this.callbackMatch}
									visible={this.state.brandData?.matchVisibility || false}
									brandId={this.brandId} />
							</Box>
						}
					</Box>
				}
				{
					this.state.currentTab === "2" && !this.state.loading && this.state.brandData &&
					<Representatives representativeList={this.state.brandData?.representatives} submitRepresentative={this.updateRepresentative} />
				}
			</Box>
		)
	}
}

export default withUseRouter(BrandProfile)
